type AnyFunction = (...arguments_: never[]) => unknown

export type DebouncedFunction<F extends AnyFunction> = {
  (...arguments_: Parameters<F>): ReturnType<F> | undefined
  clear(): void
  flush(): void
}

/**
Creates a debounced function that delays execution until `wait` milliseconds have passed since its last invocation.

Set the `immediate` option to `true` to invoke the function immediately at the start of the `wait` interval, preventing issues such as double-clicks on a button.

The returned function has a `.clear()` method to cancel scheduled executions, and a `.flush()` method for immediate execution and resetting the timer for future calls.
*/

export function debounce<F extends AnyFunction>(function_: F, wait = 100, options: { immediate?: boolean } = {}) {
  if (typeof function_ !== 'function') {
    throw new TypeError(`Expected the first parameter to be a function, got \`${typeof function_}\`.`)
  }

  if (wait < 0) {
    throw new RangeError('`wait` must not be negative.')
  }

  const { immediate = false } = options

  let storedContext: unknown
  let storedArguments: Parameters<F> | undefined
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  let timestamp = 0
  let result: ReturnType<F> | undefined
  const run = (context: unknown, arguments_: Parameters<F> | undefined) =>
    Reflect.apply(function_, context, arguments_ ?? []) as ReturnType<F>

  function later() {
    const last = Date.now() - timestamp

    if (last < wait && last >= 0) {
      timeoutId = setTimeout(later, wait - last)
    } else {
      timeoutId = undefined

      if (!immediate) {
        const callContext = storedContext
        const callArguments = storedArguments
        storedContext = undefined
        storedArguments = undefined
        result = run(callContext, callArguments)
      }
    }
  }

  const debounced = function (this: unknown, ...arguments_: Parameters<F>) {
    if (storedContext && this !== storedContext) {
      throw new Error('Debounced method called with different contexts.')
    }

    storedContext = this // eslint-disable-line @typescript-eslint/no-this-alias
    storedArguments = arguments_
    timestamp = Date.now()

    const callNow = immediate && !timeoutId

    if (!timeoutId) {
      timeoutId = setTimeout(later, wait)
    }

    if (callNow) {
      const callContext = storedContext
      const callArguments = storedArguments
      storedContext = undefined
      storedArguments = undefined
      result = run(callContext, callArguments)
    }

    return result
  }

  debounced.clear = () => {
    if (!timeoutId) {
      return
    }

    clearTimeout(timeoutId)
    timeoutId = undefined
  }

  debounced.flush = () => {
    if (!timeoutId) {
      return
    }

    const callContext = storedContext
    const callArguments = storedArguments
    storedContext = undefined
    storedArguments = undefined
    result = run(callContext, callArguments)

    clearTimeout(timeoutId)
    timeoutId = undefined
  }

  return debounced
}
